import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { db } from "../utils/offlineDB";
import { env } from "../constants/env";

/**
 * Offline-first Accreditation Hook
 * Always POST to /api/accreditation for creation or update
 */
export const useAccreditation = ({
  labId,
  section,
  name, // "SADCAS F134-A" or "SADCAS F134-B"
  auditorId,
  token,
  accreditationId, // optional (offline id)
  isAssessor = false,
  isEditing = false,
}) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [editable, setEditable] = useState(isAssessor);
  const [fetching, setFetching] = useState(false);

  /* --------------------------------------------
   * 1️⃣ Hydrate form (OFFLINE → ONLINE fallback)
   * ------------------------------------------ */
  useEffect(() => {
    let mounted = true;

    const hydrate = async () => {
      setFetching(true);
      // Try offline first
      const local = accreditationId
        ? await db.offlineAccreditations.get(accreditationId)
        : await db.offlineAccreditations
            .where("[labId+section+name]")
            .equals([labId, section, name])
            .first();

      console.log("WHAT IS ON LOCAL", local);

      if (local && mounted) {
        setFormData(
          local.answers?.reduce((acc, ans) => {
            acc[ans.questionId] = {
              score: ans.score,
              comment: ans.comment,
              points: ans.points,
              content: ans.content,
            };
            return acc;
          }, {}) || {}
        );

        // Determine editability
        if (local.auditorId !== auditorId || local.answers?.length > 0) {
          setEditable(false);
        }

        setHydrated(true);
        setFetching(false);
        return;
      }

      // Stop if offline
      if (!navigator.onLine) {
        // Determine editability
        if (isAssessor) {
          setEditable(true);
        }
        setFetching(false);
        setHydrated(true);
        return;
      }

      // Fetch from API
      try {
        const res = await axios.get(`${env.kataApiUrl}/api/accreditation`, {
          params: { labId, section, name },
          headers: { Authorization: `Bearer ${token}` },
        });

        const acc = res.data?.accreditation;

        if (!acc) return;

        console.log(
          "[Accreditation] fetched from API:",
          acc.auditorId,
          auditorId
        );

        setFormData(
          acc.answers.reduce((map, ans) => {
            map[ans.questionId] = {
              score: ans.score,
              comment: ans.comment,
              points: ans.points,
              content: ans.content,
            };
            return map;
          }, {})
        );
        if (acc.auditorId !== auditorId && isAssessor) {
          setEditable(false);
        } else {
          setEditable(true);
        }
        // Editable logic
        if (acc.answers?.length > 0 && !isEditing) {
          setEditable(false);
        }

        // Cache locally
        await db.offlineAccreditations.put({
          id: acc._id,
          labId,
          section,
          name,
          auditorId: acc.auditorId,
          answers: acc.answers,
          offline: false,
          timestamp: Date.now(),
        });
      } catch (err) {
        console.error("[Accreditation] hydrate failed", err);
      } finally {
        setHydrated(true);
        setFetching(false);
      }
    };

    hydrate();
    return () => (mounted = false);
  }, [labId, section, name, accreditationId, token, auditorId]);

  /* --------------------------------------------
   * 2️⃣ Submit handler (ONLINE / OFFLINE)
   * ------------------------------------------ */
  const handleDone = async () => {
    if (!Object.keys(formData).length) {
      toast.warning("Please answer at least one question");
      return;
    }

    setLoading(true);

    const answers = Object.entries(formData).map(([questionId, val]) => ({
      questionId,
      content: val.content || "",
      score: val.score || "NA",
      comment: val.comment,
      points: val.points || 0,
    }));

    const payload = {
      name,
      labId,
      section,
      auditorId,
      answers,
    };

    const recordId = accreditationId || `offline-${crypto.randomUUID()}`;

    try {
      /* ---------- OFFLINE ---------- */
      if (!navigator.onLine) {
        await db.pendingRequests.add({
          url: `${env.kataApiUrl}/api/accreditation`,
          method: "POST",
          body: payload,
          token,
          timestamp: Date.now(),
        });

        await db.offlineAccreditations.put({
          id: recordId,
          ...payload,
          offline: true,
          timestamp: Date.now(),
        });

        toast.info(
          "You are offline. Accreditation saved locally and will sync when online."
        );
        setEditable(false);
        return;
      }

      /* ---------- ONLINE ---------- */
      const res = await axios.post(
        `${env.kataApiUrl}/api/accreditation`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const serverData = res.data.accreditation || res.data;

      // Remove offline record if exists
      if (accreditationId?.startsWith("offline-")) {
        await db.offlineAccreditations.delete(accreditationId);
      }

      // Cache latest online record locally
      await db.offlineAccreditations.put({
        id: serverData._id,
        labId,
        section,
        name,
        auditorId: serverData.auditorId,
        answers: serverData.answers,
        offline: false,
        lastSynced: new Date().toISOString(),
        timestamp: Date.now(),
      });

      toast.success("Accreditation submitted successfully");
      setEditable(false);
    } catch (err) {
      console.error("[Accreditation] submit failed", err);
      toast.error(err.message || "Failed to submit accreditation");
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    setFormData,
    loading,
    hydrated,
    fetching,
    editable,
    setEditable,
    handleDone,
  };
};
